import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Chart,
} from "chart.js";
import { useEffect, useRef } from "react";
import React from "react";

export enum LinearGaugeType{
  Temperature = 1,
  HumidityOrMoisture=2,
  Pressure=3
}

export interface ColorCodedGaugeRange {
  start: number;
  end: number;
  color: string;
}

export interface ColorCodedGaugeOptions {
  ranges: ColorCodedGaugeRange[];
}

export interface LinearGaugeProps {
  currentValue: number | null | undefined;
  colorCodedGaugeExists?: boolean;
  colorCodedGaugeOptions?: ColorCodedGaugeOptions;
  scaleData: number[];
  type: LinearGaugeType;
  needleAnimationSpeed: number;
}

const LinearGauge : React.FC<LinearGaugeProps> = ({ currentValue, colorCodedGaugeExists, scaleData, type, colorCodedGaugeOptions, needleAnimationSpeed }) => {

  ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
  );
  
  const gaugeScaleData = useRef<number[]>(scaleData);
  
  const chartRef = useRef(null);
  const isAnimating = useRef<boolean>(false); 
  const needleValue = useRef<number>(0);

  useEffect( () => {
    isAnimating.current = true;
    animateNeedle();
  }, [currentValue]);

  const animateNeedle = () => {

    const speed = needleAnimationSpeed;

    if(currentValue != null){
      if(isAnimating.current === true){
        if (needleValue.current < currentValue) {
          needleValue.current = Math.min(needleValue.current + speed, currentValue); // Move up towards target
        } else if (needleValue.current > currentValue) {
          needleValue.current = Math.max(needleValue.current - speed, currentValue); // Move down towards target
        }
    
        (chartRef.current as any)?.update('none'); 
    
        // Continue the animation if needle hasn't reached target
        if (needleValue.current !== currentValue) {
          requestAnimationFrame(animateNeedle);
        }else{
          isAnimating.current = false;
        }
      }else{
        needleValue.current = currentValue;
      }
    }
  } 

  useEffect( () => {
    gaugeScaleData.current = scaleData;
    isAnimating.current = false;
    needleValue.current = currentValue ?? 0;
    const chartInstance = chartRef.current as any;
    if(chartInstance != null){
      chartInstance.render();
    } 
  }, [scaleData]);

  const topPadding = 15;
  const rightPadding = 30;

  const data = {
    labels: [''],
    datasets: [
      {
        label: '',
        data: [gaugeScaleData.current.length-1],
        scaleData: scaleData,
        backgroundColor: ["white"],
        borderColor: ["white"],
        borderWidth: 1,
        barPercentage: 0.05,
        borderSkipped: false,
      },
    ],
  };

  const colorCodedGauge = {
    id:'colorCodedGauge',
    afterDatasetsDraw(chart: Chart, args: any, plugins: any){

      if(colorCodedGaugeExists && (colorCodedGaugeOptions != null) && (colorCodedGaugeOptions.ranges.length > 0)){
        const { ctx, data } = chart;
        
        ctx.save();
        const chartDatasetMetaFirst = chart.getDatasetMeta(0).data[0];
        const xPos = chartDatasetMetaFirst.x;
        const yPos = chartDatasetMetaFirst.y;
        const barThickness = (chartDatasetMetaFirst as any)?.width / 2.0; //is actually BarElement but idk why typescript is failing
        const singleUnit = chart.chartArea.height / gaugeScaleData.current[gaugeScaleData.current.length-1];
  
        const gaugeThickness = 30;
        const gaugeDistanceFromAxis = 20;
  
        ctx.font = 'bold 12px sans-serif';
        ctx.fillStyle = '#006633';
        ctx.textBaseline = 'middle';
        ctx.textAlign = 'center';
  
        // Set line color and thickness
        // ctx.strokeStyle = 'white';
        ctx.lineWidth = 2; 
  
        ctx.translate(xPos, chart.chartArea.bottom); 
  
        const rangeLength = colorCodedGaugeOptions?.ranges.length ?? 0;
        for(let i = 0; i < rangeLength; i++){
          let optionsObject = colorCodedGaugeOptions?.ranges[i];
  
          ctx.fillStyle = optionsObject?.color;
  
          ctx.beginPath();
          ctx.moveTo(barThickness - gaugeDistanceFromAxis, (yPos - singleUnit * optionsObject?.start)-topPadding); // get to starting point 
          ctx.lineTo(barThickness - (gaugeDistanceFromAxis + gaugeThickness), (yPos - singleUnit * optionsObject?.start)- topPadding); // go left
          ctx.lineTo(barThickness - (gaugeDistanceFromAxis + gaugeThickness), (yPos - singleUnit * optionsObject?.end)- topPadding); // go up
          ctx.lineTo(barThickness - gaugeDistanceFromAxis, (yPos - singleUnit * optionsObject?.end)-topPadding); // go right
          ctx.lineTo(barThickness - gaugeDistanceFromAxis, (yPos - singleUnit * optionsObject?.start)-topPadding); //back to starting point
          ctx.fill();
        }

        ctx.restore();
      }
    }
  }

  const gaugeNeedle = {
    id:'gaugeNeedle',
    afterDatasetsDraw(chart: Chart, args: any, plugins: any){
      const { ctx, data } = chart;
        
      ctx.save();
      const chartDatasetMetaFirst = chart.getDatasetMeta(0).data[0];
      const xPos = chartDatasetMetaFirst.x;
      const yPos = chartDatasetMetaFirst.y;
      const barThickness = (chartDatasetMetaFirst as any)?.width / 2.0; //is actually BarElement but idk why typescript is failing
      const singleUnit = chart.chartArea.height / gaugeScaleData.current[gaugeScaleData.current.length-1];

      const needleBaseLength = 30;
      const needleHeight = 40;
      const needleDistanceFromAxis = 10;

      ctx.font = 'bold 12px sans-serif';
      ctx.fillStyle = 'white';
      ctx.textBaseline = 'middle';
      ctx.textAlign = 'center';

      // Set line color and thickness
      // ctx.strokeStyle = 'white';
      ctx.lineWidth = 2; 
      
      ctx.translate(xPos, chart.chartArea.bottom);  
      
      ctx.beginPath();
      ctx.moveTo(barThickness - needleDistanceFromAxis, (yPos - singleUnit * needleValue.current) - topPadding); // Tip of the needle
      ctx.lineTo(barThickness - (needleDistanceFromAxis + needleHeight), (yPos - singleUnit * needleValue.current - (needleBaseLength / 2.0)) - topPadding); // Top left of the needle
      ctx.lineTo(barThickness - (needleDistanceFromAxis + needleHeight), (yPos - singleUnit * needleValue.current + (needleBaseLength / 2.0)) - topPadding); // Bottom left of the needle
      ctx.lineTo(barThickness - needleDistanceFromAxis, (yPos - singleUnit * needleValue.current) - topPadding);

      ctx.closePath();
      ctx.fillStyle = '#42f58a'; 
      ctx.fill();
      ctx.strokeStyle = 'black'; 
      ctx.lineWidth = 2;
      // ctx.stroke();

      ctx.restore();
    }
  } 

  const lines = {
    id: 'lines',
    afterDatasetsDraw(chart : Chart, args: any, plugins: any) {
        const { ctx, data } = chart;
      
        ctx.save();
        const chartDatasetMetaFirst = chart.getDatasetMeta(0).data[0];
        const xPos = chartDatasetMetaFirst.x;
        const yPos = chartDatasetMetaFirst.y;
        const barThickness = (chartDatasetMetaFirst as any)?.width / 2.0; //is actually BarElement but idk why typescript is failing
        const singleUnit = chart.chartArea.height / gaugeScaleData.current[gaugeScaleData.current.length-1];
      
        const lineLength = 20;  
        const textDistanceFromLine = 20;

        const smallTickDistanceFromLine = 5;
        const smallTickLength = 10;
        let suffix = "";
        switch(type){
          case LinearGaugeType.Temperature:
            suffix="°";
            break;
          case LinearGaugeType.HumidityOrMoisture:
            suffix="%";
            break;
          case LinearGaugeType.Pressure:
        }

        ctx.font = 'bold 13px sans-serif';
        ctx.fillStyle = 'white';
        ctx.textBaseline = 'middle';
        ctx.textAlign = 'center';
    
        // Set line color and thickness
        ctx.strokeStyle = 'white';
        ctx.lineWidth = 3; 
    
        ctx.translate(xPos, chart.chartArea.bottom);  

        for(let i =0; i < scaleData.length; i++){
          let sd = gaugeScaleData.current[i];

          ctx.lineWidth = 3; 
          let scaleTickStart = barThickness;
          let scaleTickEndOffset = 0;
          if(i === 0 || i === scaleData.length-1){
            scaleTickStart = -3;
            scaleTickEndOffset = 6;
          }
          ctx.beginPath();
          ctx.moveTo(scaleTickStart, (yPos - singleUnit * sd) - topPadding);
          ctx.lineTo(scaleTickStart + lineLength + scaleTickEndOffset, (yPos - singleUnit * sd) - topPadding);
          ctx.stroke();
          ctx.fillText(`${sd}${suffix}`, barThickness + (lineLength+textDistanceFromLine), yPos-singleUnit * sd - topPadding);

          ctx.lineWidth = 1;

          if(i < scaleData.length - 1){
            if(type === LinearGaugeType.HumidityOrMoisture){
              const yTickPos = (gaugeScaleData.current[i] + gaugeScaleData.current[i+1]) / 2;
              ctx.beginPath();
              ctx.moveTo(barThickness + smallTickDistanceFromLine, (yPos - singleUnit *  yTickPos) - topPadding);
              ctx.lineTo(barThickness + (smallTickDistanceFromLine + smallTickLength), (yPos - singleUnit * yTickPos) - topPadding);
              ctx.stroke();
            }else if(type === LinearGaugeType.Temperature){
              
              const yTick1Pos = gaugeScaleData.current[i] + ( (gaugeScaleData.current[i+1] - gaugeScaleData.current[i]) / 3);
              const yTick2Pos = gaugeScaleData.current[i] + 2*( (gaugeScaleData.current[i+1] - gaugeScaleData.current[i]) / 3);

              ctx.beginPath();
              ctx.moveTo(barThickness + smallTickDistanceFromLine, (yPos - singleUnit *  yTick1Pos) - topPadding);
              ctx.lineTo(barThickness + (smallTickDistanceFromLine + smallTickLength), (yPos - singleUnit * yTick1Pos) - topPadding);
              ctx.stroke();
              
              ctx.beginPath();
              ctx.moveTo(barThickness + smallTickDistanceFromLine, (yPos - singleUnit *  yTick2Pos) - topPadding);
              ctx.lineTo(barThickness + (smallTickDistanceFromLine + smallTickLength), (yPos - singleUnit * yTick2Pos) - topPadding);
              ctx.stroke();
            }
          }
        }
        
        ctx.restore();
    }
  }



  const existingPlugins = colorCodedGaugeExists ? [colorCodedGauge,  gaugeNeedle, lines] : [gaugeNeedle, lines];

  return (
    <Bar
    ref={chartRef}
    data={data}
    // redraw={true}
    options={{
      animation: false,
      responsive: true,
      maintainAspectRatio: false,
      backgroundColor:"navy",
      layout: {
        padding:{
          top: topPadding, right: rightPadding
        }
      },
      aspectRatio: 1,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
            enabled: false
        }
      },
      scales: {
        x: {
          grid: {
            display: false,
          },
          border: {
            display: false,
          },
        },
        y: {
          grid: {
            display: false,
          },
          border: {
            display: false,
          },
          ticks: {
            display: false,
          },
        },
      },
    }}
    plugins={existingPlugins}
    />
  );
};

export default LinearGauge;
